import type { DownloadManagerJob } from '@/lib/download-manager-state';

type AutoDeliveryCoordinatorOptions = {
  deliver: (job: DownloadManagerJob) => Promise<void>;
  acknowledge: (jobId: string) => Promise<void>;
  onDeliveryStateChange?: (isDelivering: boolean) => void;
  onIntentConsumed?: (jobId: string) => void;
  onTerminalWithoutDelivery?: (job: DownloadManagerJob) => Promise<void>;
};

export type AutoDeliveryCoordinator = {
  deliverManually: (job: DownloadManagerJob) => Promise<void>;
  register: (jobId: string) => void;
  updateOptions: (options: AutoDeliveryCoordinatorOptions) => void;
  updateJobs: (jobs: DownloadManagerJob[]) => Promise<void>;
};

export function createAutoDeliveryCoordinator(
  options: AutoDeliveryCoordinatorOptions
): AutoDeliveryCoordinator {
  let currentOptions = options;
  const pendingJobIds = new Set<string>();
  const consumedJobIds = new Set<string>();
  const activeDeliveries = new Map<string, Promise<void>>();
  let latestJobs = new Map<string, DownloadManagerJob>();
  let deliveryQueue = Promise.resolve();

  const deliverExclusively = (job: DownloadManagerJob): Promise<void> => {
    const activeDelivery = activeDeliveries.get(job.id);
    if (activeDelivery) return activeDelivery;

    const delivery = (async () => {
      currentOptions.onDeliveryStateChange?.(true);
      try {
        await currentOptions.deliver(job);
        await currentOptions.acknowledge(job.id);
      } finally {
        activeDeliveries.delete(job.id);
        if (activeDeliveries.size === 0) {
          currentOptions.onDeliveryStateChange?.(false);
        }
      }
    })();
    activeDeliveries.set(job.id, delivery);
    return delivery;
  };

  const drainReadyJobs = async () => {
    const firstSettledJob = findNextSettledJob(pendingJobIds, latestJobs);
    if (!firstSettledJob) return;

    let settledJob: DownloadManagerJob | undefined = firstSettledJob;
    while (settledJob) {
      if (settledJob.status === 'ready') {
        pendingJobIds.delete(settledJob.id);
        consumedJobIds.add(settledJob.id);
        currentOptions.onIntentConsumed?.(settledJob.id);
        try {
          await deliverExclusively(settledJob);
        } catch {
          // Automatic delivery intentionally falls back to the manual action.
        }
      } else {
        await currentOptions.onTerminalWithoutDelivery?.(settledJob);
        pendingJobIds.delete(settledJob.id);
        consumedJobIds.add(settledJob.id);
        currentOptions.onIntentConsumed?.(settledJob.id);
      }
      settledJob = findNextSettledJob(pendingJobIds, latestJobs);
    }
  };

  const scheduleDrain = () => {
    deliveryQueue = deliveryQueue
      .then(drainReadyJobs, drainReadyJobs)
      .catch(() => undefined);
    return deliveryQueue;
  };

  return {
    deliverManually: deliverExclusively,
    register: (jobId) => {
      if (consumedJobIds.has(jobId)) return;
      pendingJobIds.add(jobId);
      void scheduleDrain();
    },
    updateOptions: (nextOptions) => {
      currentOptions = nextOptions;
    },
    updateJobs: async (jobs) => {
      latestJobs = new Map(jobs.map((job) => [job.id, job]));
      await scheduleDrain();
    },
  };
}

function findNextSettledJob(
  pendingJobIds: Set<string>,
  jobsById: Map<string, DownloadManagerJob>
): DownloadManagerJob | undefined {
  for (const jobId of pendingJobIds) {
    const job = jobsById.get(jobId);
    if (
      job?.status === 'ready' ||
      job?.status === 'failed' ||
      job?.status === 'expired' ||
      job?.status === 'cancelled'
    ) {
      return job;
    }
  }
  return undefined;
}
